/**
 * Funktionstrennung — Regeln, Befunde und die Vorschau.
 *
 * Gegenstück zu `werkzeuge/sod.mjs` und den vier Endpunkten, die der
 * Entwicklungsserver seit dem 13.08.2026 bedient. Bis dahin las `/sod` seine
 * sieben Regeln samt Konfliktzahlen aus `entwurf/beispieldaten.ts` — die Zahlen
 * standen als Zeichenketten im Quelltext und waren an nichts gebunden: keine
 * Änderung am Bestand konnte sie bewegen.
 *
 * ## Was die Zahlen bedeuten
 *
 * Ein Befund entsteht, wenn die **Standardrolle** einer Person beide Seiten
 * einer Regel trifft. Das ist die Soll-Sicht aus der Rollenmatrix, nicht der
 * tatsächlich erteilte Zugriff: `iam.SodBefund` verweist auf `iam.Zugriff`,
 * und Zugriffsdaten gibt es noch nicht. Der Unterschied ist fachlich erheblich
 * — die Ansicht benennt ihn, statt ihn zu verschweigen.
 *
 * ## Wo es heute rechnet
 *
 * Ausschliesslich im **Entwicklungsserver**. `apps/api` kennt weder Sammlung
 * noch Routen; dort fehlt zusätzlich die SQL-Variante der Berechnung. Gegen die
 * ausgelieferte Schnittstelle antwortet jeder dieser Pfade deshalb mit einem
 * Fehler, und die Ansicht zeigt ihn als solchen an — sie erfindet keinen
 * Ersatzbestand.
 */

import { senden, type Seite } from './klient.js';
import { useAbruf, type Zustand } from './haken.js';
import { tokenHolen } from '../anmeldung/msal.js';

/* ------------------------------------------------------------------ Formen */

export const SCHWEREGRADE = ['gering', 'mittel', 'hoch', 'sehr hoch'] as const;
export type Schweregrad = (typeof SCHWEREGRADE)[number];

/**
 * Eine Bedingung einer Regelseite.
 *
 * `berechtigungsart` ist freiwillig: ohne Angabe trifft jede Art auf diesem
 * Asset. „vSphere überhaupt" ist eine andere Aussage als „vSphere mit
 * Adminrecht", und beide werden gebraucht.
 */
export interface Seitenbedingung {
  asset: string;
  berechtigungsart?: string | null;
}

/** Eine Regel, wie die Schnittstelle sie ausgibt — mit gerechneter Konfliktzahl. */
export interface SodRegel {
  schluessel: string;
  bezeichnung: string;
  beschreibung: string | null;
  schweregrad: string;
  /** Blockiert Anträge. Nur bei Schweregrad hoch oder sehr hoch zulässig. */
  blockierend: boolean;
  kontrolle: string;
  seiteA: Seitenbedingung[];
  seiteB: Seitenbedingung[];
  aktiv: boolean;
  /** Bei **jedem** Abruf neu aus dem Bestand gerechnet, nirgends gespeichert. */
  konflikte: number;
}

/** Ein Befund — eine Person, deren Rolle beide Seiten einer Regel trifft. */
export interface SodBefund {
  /** Schlüssel der Regel, z. B. `SOD-001`. */
  regel: string;
  bezeichnung: string;
  kontrolle: string;
  person: string;
  personId: string;
  oeCode: string;
  rolleId: string;
  /** Die treffende Zuordnung als „Asset · Berechtigungsart". */
  seiteA: string;
  seiteB: string;
  schweregrad: string;
  zustand: string;
}

/** Was eine Regel bewirken **würde** — gerechnet, bevor sie gespeichert wird. */
export interface SodVorschau {
  konflikte: number;
  rollen: string[];
  /** Höchstens fünf; sie machen die Zahl herleitbar. */
  beispiele: SodBefund[];
}

/** Der Entwurf einer Regel, wie das Formular ihn schickt. */
export interface Regelentwurf {
  schluessel: string;
  bezeichnung: string;
  beschreibung: string;
  schweregrad: string;
  blockierend: boolean;
  kontrolle: string;
  seiteA: Seitenbedingung[];
  seiteB: Seitenbedingung[];
}

/* ------------------------------------------------------------------- Lesen */

/**
 * Alle Regeln des Mandanten samt ihrer aktuellen Konfliktzahl.
 *
 * `aus` schaltet den Abruf ab — für Ansichten, die die Regeln nur in einem
 * Reiter brauchen. Ohne den Schalter zöge jeder Seitenaufruf eine Liste, die
 * niemand aufschlägt.
 */
export const useSodRegeln = (mandant: string, aus = false): Zustand<Seite<SodRegel>> =>
  useAbruf<Seite<SodRegel>>(aus ? null : '/api/sod/regeln', mandant);

/**
 * Die Befunde aller aktiven Regeln, wahlweise auf eine Regel oder eine Rolle
 * eingeschränkt.
 *
 * Beide Filter laufen serverseitig (`?regel=…`, `?rolle=…`) und nicht im
 * Browser: für eine Rolle mit drei Befunden alle zweihundert zu übertragen und
 * dann 197 wegzuwerfen, wäre bei einem echten Bestand der ganze Nachweis, der
 * über die Leitung geht.
 */
export const useSodBefunde = (
  mandant: string,
  einschraenkung?: { regel?: string | null; rolle?: string | null; aus?: boolean } | null,
): Zustand<Seite<SodBefund>> => {
  const p = new URLSearchParams();
  if (einschraenkung?.regel) p.set('regel', einschraenkung.regel);
  if (einschraenkung?.rolle) p.set('rolle', einschraenkung.rolle);
  const abfrage = p.toString();
  return useAbruf<Seite<SodBefund>>(
    einschraenkung?.aus ? null
      : abfrage ? `/api/sod/befunde?${abfrage}` : '/api/sod/befunde',
    mandant,
  );
};

/* --------------------------------------------------------------- Schreiben */

/**
 * Rechnet, was eine Regel treffen würde — **vor** dem Speichern.
 *
 * Der frühere Hinweistext versprach genau das („Diese Regel würde aktuell 7
 * Konflikte erzeugen"), ohne dass irgendetwas gerechnet hätte. Die Sieben war
 * eine Zeichenkette.
 */
export const regelVorschau = async (
  mandant: string,
  entwurf: Regelentwurf,
): Promise<SodVorschau> =>
  senden<SodVorschau>('/api/sod/vorschau', 'POST', {
    mandant, token: await tokenHolen(), rumpf: entwurf,
  });

/**
 * Legt eine Regel an.
 *
 * Der Server weist dieselben zwei Bedingungen ab, die `014-sod.sql` als CHECK
 * führt — blockierend nur ab Schweregrad hoch, und beide Seiten dürfen nicht
 * gleich sein. Die Ansicht nennt den Grund damit, bevor die Datenbank ihn als
 * Meldung 547 zurückwirft.
 */
export const regelAnlegen = async (
  mandant: string,
  entwurf: Regelentwurf,
): Promise<SodRegel> =>
  senden<SodRegel>('/api/sod/regeln', 'POST', {
    mandant, token: await tokenHolen(), rumpf: entwurf,
  });

/* ------------------------------------------------------------------ Helfer */

/** Klasse des Markers zum Schweregrad — dieselben vier Stufen wie die Kritikalität. */
export function gradKlasse(grad: string): string {
  if (grad === 'sehr hoch') return 'sehrhoch';
  if (grad === 'hoch') return 'hoch';
  if (grad === 'mittel') return 'mittel';
  return 'gering';
}

/** Eine Seite als Text: „vSphere · Adminrecht, Veeam". */
export function seiteText(seite: Seitenbedingung[]): string {
  return seite
    .map((b) => (b.berechtigungsart ? `${b.asset} · ${b.berechtigungsart}` : b.asset))
    .join(', ');
}
